import { useState, useEffect, useCallback } from 'react';
import { useLanguage } from '../contexts/LanguageContext';
import { useAuth } from '../contexts/AuthContext';
import Navbar from '../components/Navbar';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Switch } from '../components/ui/switch';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '../components/ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../components/ui/alert-dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { toast } from 'sonner';
import axios from 'axios';
import {
  Plus, Pencil, Trash2, Loader2, Database, Crown, Server, Send, Globe,
  Copy, Check, Link as LinkIcon, Wifi,
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const TELEGRAM_URL = "https://t.me/DZ_CT";

// DNS record type config
const RECORD_TYPE_CONFIG = {
  A:     { icon: Globe,     dot: 'bg-blue-500',    text: 'text-blue-500' },
  AAAA:  { icon: Wifi,      dot: 'bg-purple-500',  text: 'text-purple-500' },
  CNAME: { icon: LinkIcon,  dot: 'bg-emerald-500', text: 'text-emerald-500' },
  NS:    { icon: Server,    dot: 'bg-orange-500',  text: 'text-orange-500' },
};

function RecordTypeBadge({ type }) {
  const config = RECORD_TYPE_CONFIG[type] || RECORD_TYPE_CONFIG.A;
  const Icon = config.icon;
  return (
    <span
      className="inline-flex items-center gap-1 px-2 py-0.5 rounded border border-border/50 bg-background/40 font-mono text-[10px] tracking-wider text-foreground/80"
      data-testid={`record-type-badge-${type}`}
    >
      <Icon className={`h-3 w-3 ${config.text}`} />
      {type}
    </span>
  );
}

function CopyButton({ text }) {
  const [copied, setCopied] = useState(false);
  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(true);
      toast.success('copied');
      setTimeout(() => setCopied(false), 1500);
    } catch {
      toast.error('copy failed');
    }
  };
  return (
    <Button
      variant="ghost"
      size="icon"
      className="h-6 w-6 shrink-0 rounded text-muted-foreground hover:text-primary"
      onClick={handleCopy}
      data-testid="copy-record-btn"
    >
      {copied
        ? <Check className="h-3 w-3 text-primary animate-copy-flash" />
        : <Copy className="h-3 w-3" />}
    </Button>
  );
}

function StatTile({ label, value, sub, icon: Icon }) {
  return (
    <div className="rounded border border-border/40 bg-card/40 p-4 hover:border-primary/30 transition-colors">
      <div className="flex items-start justify-between mb-3">
        <span className="font-mono text-[9px] tracking-[0.2em] uppercase text-muted-foreground">
          {label}
        </span>
        <Icon className="h-3.5 w-3.5 text-primary/70" />
      </div>
      <div className="font-heading text-2xl font-bold leading-none">{value}</div>
      {sub && <div className="font-mono text-[10px] text-muted-foreground mt-2">{sub}</div>}
    </div>
  );
}

export default function Dashboard() {
  const { t } = useLanguage();
  const { user, token } = useAuth();
  const [records, setRecords] = useState([]);
  const [loading, setLoading] = useState(true);
  const [userStats, setUserStats] = useState(null);
  const [domains, setDomains] = useState([]);

  // Create dialog
  const [createOpen, setCreateOpen] = useState(false);
  const [createForm, setCreateForm] = useState({ record_type: 'A', name: '', content: '', domain_id: '', ttl: 1, proxied: false });
  const [createLoading, setCreateLoading] = useState(false);

  // Edit dialog
  const [editOpen, setEditOpen] = useState(false);
  const [editRecord, setEditRecord] = useState(null);
  const [editForm, setEditForm] = useState({ content: '', ttl: 1, proxied: false });
  const [editLoading, setEditLoading] = useState(false);

  // Delete dialog
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [deleteRecord, setDeleteRecord] = useState(null);
  const [deleteLoading, setDeleteLoading] = useState(false);

  const getHeaders = useCallback(() => ({ Authorization: `Bearer ${token}` }), [token]);

  const fetchRecords = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/dns/records`, { headers: getHeaders() });
      setRecords(res.data.records || []);
    } catch {
      toast.error('failed to load records');
    } finally {
      setLoading(false);
    }
  }, [getHeaders]);

  const fetchStats = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/auth/me`, { headers: getHeaders() });
      setUserStats(res.data);
    } catch { /* ignore */ }
  }, [getHeaders]);

  const fetchDomains = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/domains`, { headers: getHeaders() });
      const activeDomains = res.data.domains || [];
      setDomains(activeDomains);
      setCreateForm(prev => prev.domain_id || activeDomains.length === 0
        ? prev
        : { ...prev, domain_id: activeDomains[0].id });
    } catch { /* ignore */ }
  }, [getHeaders]);

  useEffect(() => {
    fetchRecords();
    fetchStats();
    fetchDomains();
  }, [fetchRecords, fetchStats, fetchDomains]);

  const handleCreate = async (e) => {
    e.preventDefault();
    setCreateLoading(true);
    try {
      await axios.post(`${API}/dns/records`, createForm, { headers: getHeaders() });
      toast.success('record created');
      setCreateOpen(false);
      setCreateForm(prev => ({ record_type: 'A', name: '', content: '', domain_id: prev.domain_id, ttl: 1, proxied: false }));
      fetchRecords();
      fetchStats();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'failed to create');
    } finally {
      setCreateLoading(false);
    }
  };

  const handleEdit = async (e) => {
    e.preventDefault();
    setEditLoading(true);
    try {
      await axios.put(`${API}/dns/records/${editRecord.id}`, editForm, { headers: getHeaders() });
      toast.success('record updated');
      setEditOpen(false);
      fetchRecords();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'failed to update');
    } finally {
      setEditLoading(false);
    }
  };

  const handleDelete = async () => {
    setDeleteLoading(true);
    try {
      await axios.delete(`${API}/dns/records/${deleteRecord.id}`, { headers: getHeaders() });
      toast.success('record deleted');
      setDeleteOpen(false);
      fetchRecords();
      fetchStats();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'failed to delete');
    } finally {
      setDeleteLoading(false);
    }
  };

  const openEdit = (record) => {
    setEditRecord(record);
    setEditForm({ content: record.content, ttl: record.ttl || 1, proxied: record.proxied || false });
    setEditOpen(true);
  };

  const openDelete = (record) => {
    setDeleteRecord(record);
    setDeleteOpen(true);
  };

  const getContentPlaceholder = (type) => {
    if (type === 'A') return t('dashboard.content_placeholder_a');
    if (type === 'AAAA') return t('dashboard.content_placeholder_aaaa');
    if (type === 'NS') return t('dashboard.content_placeholder_ns');
    return t('dashboard.content_placeholder_cname');
  };

  const selectedDomain = domains.find(d => d.id === createForm.domain_id);
  const selectedDomainName = selectedDomain?.name || 'dnslab.biz';

  const recordLimit = userStats?.record_limit === -1 ? '∞' : (userStats?.record_limit ?? 2);
  const canCreate = userStats?.record_limit === -1 || (userStats?.record_count ?? 0) < (userStats?.record_limit ?? 2);
  const planLabel = (userStats?.plan || 'free').toLowerCase();

  return (
    <div className="min-h-screen bg-background page-transition" data-testid="dashboard-page">
      <Navbar />
      <div className="max-w-6xl mx-auto px-4 sm:px-6 pt-20 pb-12">
        {/* Header */}
        <div className="flex flex-col sm:flex-row items-start sm:items-end justify-between gap-4 mb-8 animate-fade-in-up">
          <div>
            <div className="text-[10px] text-primary tracking-wider mb-2">$ dns --list</div>
            <h1 className="font-heading text-2xl font-bold tracking-tight" data-testid="dashboard-title">
              {t('dashboard.title')}
            </h1>
            <p className="text-[11px] text-muted-foreground mt-1 font-mono" dir="ltr">
              <span className="text-primary/70">user@dnslab</span>:~$ {user?.email}
            </p>
          </div>
          <div className="flex items-center gap-2">
            {!canCreate && (
              <a href={TELEGRAM_URL} target="_blank" rel="noopener noreferrer">
                <Button variant="outline" size="sm" className="gap-1.5 rounded h-8 text-[10px] tracking-wider border-border/50" data-testid="upgrade-btn">
                  <Send className="h-3 w-3" />
                  {t('dashboard.upgrade')}
                </Button>
              </a>
            )}
            <Button
              onClick={() => setCreateOpen(true)}
              disabled={!canCreate}
              size="sm"
              className="gap-1.5 rounded h-8 text-[10px] tracking-wider"
              data-testid="add-record-btn"
            >
              <Plus className="h-3 w-3" />
              {t('dashboard.add_record')}
            </Button>
          </div>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mb-8 animate-fade-in-up delay-100">
          <StatTile
            icon={Database}
            label={t('dashboard.stats.total_records')}
            value={userStats?.record_count ?? 0}
            sub={`${userStats?.record_count ?? 0}/${recordLimit}`}
          />
          <StatTile
            icon={Server}
            label={t('dashboard.stats.record_limit')}
            value={recordLimit === '∞' ? <span className="text-primary glow-green">∞</span> : recordLimit}
            sub={recordLimit === '∞' ? t('dashboard.stats.unlimited') : null}
          />
          <StatTile
            icon={Crown}
            label={t('dashboard.stats.plan_type')}
            value={
              <span className={planLabel === 'free' ? 'text-foreground' : 'text-primary glow-green'}>
                {planLabel}
              </span>
            }
            sub={planLabel === 'free' ? '// free tier' : '// premium'}
          />
        </div>

        {/* Records Section */}
        <div className="animate-fade-in-up delay-200">
          <div className="flex items-center justify-between mb-3 px-1">
            <div className="text-[10px] text-primary tracking-wider">$ records --all</div>
            <span className="font-mono text-[9px] tracking-wider text-muted-foreground" dir="ltr">
              ~/zone/{selectedDomainName}
            </span>
          </div>

          <div className="rounded border border-border/40 bg-card/40 overflow-hidden">
            {loading ? (
              <div className="flex items-center justify-center py-16">
                <Loader2 className="h-5 w-5 animate-spin text-primary" />
              </div>
            ) : records.length === 0 ? (
              <div className="text-center py-16 text-muted-foreground" data-testid="no-records-msg">
                <Database className="h-8 w-8 mx-auto mb-3 opacity-30" />
                <p className="text-xs font-mono">{t('dashboard.no_records')}</p>
                <p className="text-[10px] text-muted-foreground/60 mt-2 font-mono">// $ click "{t('dashboard.add_record')}" to begin</p>
              </div>
            ) : (
              <>
                {/* Mobile Card View */}
                <div className="block sm:hidden divide-y divide-border/30">
                  {records.map((record, index) => (
                    <div
                      key={record.id}
                      data-testid={`record-card-${record.id}`}
                      className="p-4 space-y-2.5 animate-record-in"
                      style={{ animationDelay: `${index * 50}ms` }}
                    >
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2 min-w-0 flex-1">
                          <RecordTypeBadge type={record.record_type} />
                          <span className="font-mono text-xs truncate" dir="ltr">
                            {record.name}<span className="text-muted-foreground">.{record.domain_name || 'dnslab.biz'}</span>
                          </span>
                          <CopyButton text={`${record.name}.${record.domain_name || 'dnslab.biz'}`} />
                        </div>
                        <div className="flex items-center gap-0.5 shrink-0">
                          <Button variant="ghost" size="icon" className="h-7 w-7 rounded text-muted-foreground hover:text-primary" onClick={() => openEdit(record)} data-testid={`edit-record-mobile-${record.id}`}>
                            <Pencil className="h-3 w-3" />
                          </Button>
                          <Button variant="ghost" size="icon" className="h-7 w-7 rounded text-muted-foreground hover:text-destructive" onClick={() => openDelete(record)} data-testid={`delete-record-mobile-${record.id}`}>
                            <Trash2 className="h-3 w-3" />
                          </Button>
                        </div>
                      </div>
                      <div className="font-mono text-[11px] text-foreground/70 bg-background/50 border border-border/30 rounded px-2.5 py-1.5 break-all" dir="ltr">
                        <span className="text-primary/60">→ </span>{record.content}
                      </div>
                      <div className="flex items-center gap-3 font-mono text-[10px] text-muted-foreground">
                        <span>ttl: {record.ttl === 1 ? 'auto' : record.ttl}</span>
                        <span className="flex items-center gap-1">
                          <span className={`w-1.5 h-1.5 rounded-full ${record.proxied ? 'bg-primary animate-pulse-dot' : 'bg-muted-foreground/30'}`} />
                          {record.proxied ? 'proxied' : 'dns-only'}
                        </span>
                      </div>
                    </div>
                  ))}
                </div>

                {/* Desktop Table View */}
                <div className="hidden sm:block">
                  <table className="w-full">
                    <thead>
                      <tr className="border-b border-border/30 bg-background/30">
                        <th className="text-start px-4 py-2.5 font-mono text-[9px] tracking-[0.2em] uppercase text-muted-foreground">{t('dashboard.type')}</th>
                        <th className="text-start px-4 py-2.5 font-mono text-[9px] tracking-[0.2em] uppercase text-muted-foreground">{t('dashboard.subdomain')}</th>
                        <th className="text-start px-4 py-2.5 font-mono text-[9px] tracking-[0.2em] uppercase text-muted-foreground">{t('dashboard.content')}</th>
                        <th className="text-start px-4 py-2.5 font-mono text-[9px] tracking-[0.2em] uppercase text-muted-foreground">{t('dashboard.ttl')}</th>
                        <th className="text-start px-4 py-2.5 font-mono text-[9px] tracking-[0.2em] uppercase text-muted-foreground">{t('dashboard.proxied')}</th>
                        <th className="text-end px-4 py-2.5 font-mono text-[9px] tracking-[0.2em] uppercase text-muted-foreground">{t('dashboard.actions')}</th>
                      </tr>
                    </thead>
                    <tbody>
                      {records.map((record, index) => (
                        <tr
                          key={record.id}
                          data-testid={`record-row-${record.id}`}
                          className="border-b border-border/20 last:border-0 hover:bg-background/30 transition-colors animate-record-in"
                          style={{ animationDelay: `${index * 40}ms` }}
                        >
                          <td className="px-4 py-3">
                            <RecordTypeBadge type={record.record_type} />
                          </td>
                          <td className="px-4 py-3">
                            <div className="flex items-center gap-1.5" dir="ltr">
                              <span className="font-mono text-xs">{record.name}</span>
                              <span className="text-muted-foreground text-[10px]">.{record.domain_name || 'dnslab.biz'}</span>
                              <CopyButton text={`${record.name}.${record.domain_name || 'dnslab.biz'}`} />
                            </div>
                          </td>
                          <td className="px-4 py-3">
                            <span className="font-mono text-xs text-foreground/80" dir="ltr">{record.content}</span>
                          </td>
                          <td className="px-4 py-3 font-mono text-[10px] text-muted-foreground">
                            {record.ttl === 1 ? 'auto' : record.ttl}
                          </td>
                          <td className="px-4 py-3">
                            <span className="inline-flex items-center gap-1.5 font-mono text-[10px] text-muted-foreground">
                              <span className={`w-1.5 h-1.5 rounded-full ${record.proxied ? 'bg-primary animate-pulse-dot' : 'bg-muted-foreground/30'}`} />
                              {record.proxied ? 'on' : 'off'}
                            </span>
                          </td>
                          <td className="px-4 py-3">
                            <div className="flex items-center justify-end gap-0.5">
                              <Button
                                variant="ghost"
                                size="icon"
                                className="h-7 w-7 rounded text-muted-foreground hover:text-primary"
                                onClick={() => openEdit(record)}
                                data-testid={`edit-record-${record.id}`}
                              >
                                <Pencil className="h-3 w-3" />
                              </Button>
                              <Button
                                variant="ghost"
                                size="icon"
                                className="h-7 w-7 rounded text-muted-foreground hover:text-destructive"
                                onClick={() => openDelete(record)}
                                data-testid={`delete-record-${record.id}`}
                              >
                                <Trash2 className="h-3 w-3" />
                              </Button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </>
            )}
          </div>
        </div>
      </div>

      {/* Create Record Dialog */}
      <Dialog open={createOpen} onOpenChange={setCreateOpen}>
        <DialogContent className="rounded border-border/50 bg-card/95 backdrop-blur-md" data-testid="create-record-dialog">
          <DialogHeader>
            <div className="text-[10px] text-primary tracking-wider mb-1">$ dns --create</div>
            <DialogTitle className="font-heading text-base">{t('dashboard.create_title')}</DialogTitle>
            <DialogDescription className="sr-only">{t('dashboard.create_title')}</DialogDescription>
          </DialogHeader>
          <form onSubmit={handleCreate} className="space-y-4">
            {domains.length > 1 && (
              <div className="space-y-1.5">
                <Label className="text-[10px] text-muted-foreground tracking-wider">{t('dashboard.select_domain')}</Label>
                <Select
                  value={createForm.domain_id}
                  onValueChange={(val) => setCreateForm({ ...createForm, domain_id: val })}
                >
                  <SelectTrigger className="h-9 rounded text-xs bg-background/50 border-border/50" data-testid="create-domain-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {domains.map((d) => (
                      <SelectItem key={d.id} value={d.id} className="text-xs">
                        <span className="font-mono">{d.name}</span>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}
            <div className="space-y-1.5">
              <Label className="text-[10px] text-muted-foreground tracking-wider">{t('dashboard.record_type')}</Label>
              <Select
                value={createForm.record_type}
                onValueChange={(val) => setCreateForm({ ...createForm, record_type: val, content: '', proxied: val === 'NS' ? false : createForm.proxied })}
              >
                <SelectTrigger className="h-9 rounded text-xs bg-background/50 border-border/50" data-testid="create-type-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="A" className="text-xs font-mono">A — IPv4</SelectItem>
                  <SelectItem value="AAAA" className="text-xs font-mono">AAAA — IPv6</SelectItem>
                  <SelectItem value="CNAME" className="text-xs font-mono">CNAME — Alias</SelectItem>
                  <SelectItem value="NS" className="text-xs font-mono">NS — Nameserver</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label className="text-[10px] text-muted-foreground tracking-wider">{t('dashboard.subdomain')}</Label>
              <div className="flex items-center gap-2">
                <Input
                  value={createForm.name}
                  onChange={(e) => setCreateForm({ ...createForm, name: e.target.value })}
                  placeholder={t('dashboard.subdomain_placeholder')}
                  required
                  data-testid="create-name-input"
                  className="h-9 rounded bg-background/50 border-border/50 text-xs font-mono"
                  dir="ltr"
                />
                <span className="text-[10px] text-muted-foreground whitespace-nowrap font-mono">.{selectedDomainName}</span>
              </div>
            </div>
            <div className="space-y-1.5">
              <Label className="text-[10px] text-muted-foreground tracking-wider">{t('dashboard.content')}</Label>
              <Input
                value={createForm.content}
                onChange={(e) => setCreateForm({ ...createForm, content: e.target.value })}
                placeholder={getContentPlaceholder(createForm.record_type)}
                required
                data-testid="create-content-input"
                className="h-9 rounded bg-background/50 border-border/50 text-xs font-mono"
                dir="ltr"
              />
            </div>
            {createForm.record_type !== 'NS' && (
              <div className="flex items-center justify-between rounded border border-border/40 bg-background/30 px-3 py-2">
                <Label className="text-[11px] text-foreground/80">{t('dashboard.proxied')}</Label>
                <Switch
                  checked={createForm.proxied}
                  onCheckedChange={(val) => setCreateForm({ ...createForm, proxied: val })}
                  data-testid="create-proxied-switch"
                />
              </div>
            )}
            <DialogFooter className="gap-2">
              <Button type="button" variant="outline" className="rounded h-9 text-[11px]" onClick={() => setCreateOpen(false)}>
                {t('dashboard.cancel')}
              </Button>
              <Button type="submit" disabled={createLoading} className="rounded h-9 text-[11px] tracking-wider" data-testid="create-submit-btn">
                {createLoading ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : t('dashboard.create')}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Edit Record Dialog */}
      <Dialog open={editOpen} onOpenChange={setEditOpen}>
        <DialogContent className="rounded border-border/50 bg-card/95 backdrop-blur-md" data-testid="edit-record-dialog">
          <DialogHeader>
            <div className="text-[10px] text-primary tracking-wider mb-1">$ dns --edit</div>
            <DialogTitle className="font-heading text-base">{t('dashboard.edit_title')}</DialogTitle>
            <DialogDescription className="sr-only">{t('dashboard.edit_title')}</DialogDescription>
          </DialogHeader>
          <form onSubmit={handleEdit} className="space-y-4">
            {editRecord && (
              <div className="rounded border border-border/40 bg-background/30 p-3">
                <p className="text-[10px] text-muted-foreground tracking-wider mb-1.5">{t('dashboard.subdomain')}</p>
                <div className="flex items-center gap-2 font-mono text-xs" dir="ltr">
                  <RecordTypeBadge type={editRecord.record_type} />
                  <span>{editRecord.name}.{editRecord.domain_name || 'dnslab.biz'}</span>
                </div>
              </div>
            )}
            <div className="space-y-1.5">
              <Label className="text-[10px] text-muted-foreground tracking-wider">{t('dashboard.content')}</Label>
              <Input
                value={editForm.content}
                onChange={(e) => setEditForm({ ...editForm, content: e.target.value })}
                placeholder={editRecord ? getContentPlaceholder(editRecord.record_type) : ''}
                required
                data-testid="edit-content-input"
                className="h-9 rounded bg-background/50 border-border/50 text-xs font-mono"
                dir="ltr"
              />
            </div>
            <div className="flex items-center justify-between rounded border border-border/40 bg-background/30 px-3 py-2">
              <Label className="text-[11px] text-foreground/80">{t('dashboard.proxied')}</Label>
              <Switch
                checked={editForm.proxied}
                onCheckedChange={(val) => setEditForm({ ...editForm, proxied: val })}
                data-testid="edit-proxied-switch"
              />
            </div>
            <DialogFooter className="gap-2">
              <Button type="button" variant="outline" className="rounded h-9 text-[11px]" onClick={() => setEditOpen(false)}>
                {t('dashboard.cancel')}
              </Button>
              <Button type="submit" disabled={editLoading} className="rounded h-9 text-[11px] tracking-wider" data-testid="edit-submit-btn">
                {editLoading ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : t('dashboard.save')}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={deleteOpen} onOpenChange={setDeleteOpen}>
        <AlertDialogContent className="rounded border-border/50 bg-card/95 backdrop-blur-md" data-testid="delete-record-dialog">
          <AlertDialogHeader>
            <div className="text-[10px] text-destructive tracking-wider mb-1">$ rm -f record</div>
            <AlertDialogTitle className="font-heading text-base">{t('dashboard.delete_title')}</AlertDialogTitle>
            <AlertDialogDescription className="text-[11px]">
              {t('dashboard.delete_confirm')}
              {deleteRecord && (
                <span className="block mt-2 font-mono text-[11px] text-foreground/70 bg-background/50 border border-border/30 rounded px-2 py-1.5" dir="ltr">
                  {deleteRecord.name}.{deleteRecord.domain_name || 'dnslab.biz'} ({deleteRecord.record_type})
                </span>
              )}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter className="gap-2">
            <AlertDialogCancel className="rounded h-9 text-[11px]" data-testid="delete-cancel-btn">{t('dashboard.cancel')}</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDelete}
              disabled={deleteLoading}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90 rounded h-9 text-[11px] tracking-wider"
              data-testid="delete-confirm-btn"
            >
              {deleteLoading ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : t('dashboard.delete')}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
